/**
 * Amplitude adapter — the ONLY place in the app that touches the Amplitude SDK
 * (mirrors the single-touchpoint rule for `src/hooks/use-wallet.ts`).
 *
 * Unlike the former GA sink, this one DELIBERATELY sends `wallet_address` as a
 * user property: Amplitude is our trustworthy-signup store and the wallet is the
 * join key to Hyperliquid. Amplitude auto-stitches the anonymous
 * `device_id` to the backend `user_id` on `setUserId`, so a user's pre-login
 * sessions and post-signup activity resolve to one identity — see `ANALYTICS.md`.
 *
 * Gated by `isEnabled()` on `VITE_AMPLITUDE_API_KEY`: leave the key unset in dev
 * and the sink is a clean no-op (no dev-data pollution). Use a separate dev
 * project key if you want to see dev events.
 */
import {
	identify as amplitudeIdentify,
	init as amplitudeInit,
	reset as amplitudeReset,
	setUserId as amplitudeSetUserId,
	track as amplitudeTrack,
	Identify,
} from "@amplitude/analytics-browser";
import type { AnalyticsSink, UserTraits } from "../types";

const API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY;

let initialized = false;

/** Lazily init on first use so an unset key (dev) stays a clean no-op. */
function ensureInit(): boolean {
	if (initialized) return true;
	if (typeof window === "undefined" || !API_KEY) return false;
	amplitudeInit(API_KEY, {
		defaultTracking: {
			sessions: true,
			pageViews: false,
			formInteractions: false,
			fileDownloads: false,
		},
	});
	initialized = true;
	return true;
}

export const amplitudeSink: AnalyticsSink = {
	name: "amplitude",

	isEnabled() {
		return typeof window !== "undefined" && Boolean(API_KEY);
	},

	track(event, props) {
		if (!ensureInit()) return;
		amplitudeTrack(event, props);
	},

	page(path) {
		if (!ensureInit()) return;
		amplitudeTrack("page_view", { page_path: path });
	},

	identify(traits: UserTraits) {
		if (!ensureInit()) return;
		if (traits.user_id) amplitudeSetUserId(traits.user_id);
		const id = new Identify();
		// Mirror user_id as a property too — `setUserId` sets Amplitude's top-level
		// User ID; this also makes it queryable in property-based segments.
		if (traits.user_id) id.set("user_id", traits.user_id);
		// wallet_address is the join key to Hyperliquid — sent on purpose
		// (the former GA sink stripped it). Still never email / other PII.
		if (traits.wallet_address != null) {
			id.set("wallet_address", traits.wallet_address);
		}
		if (traits.referred_by_code != null) {
			id.set("referred_by_code", traits.referred_by_code);
		}
		if (traits.is_new_account != null) {
			id.set("is_new_account", traits.is_new_account);
		}
		if (traits.account_created_at) {
			id.set("account_created_at", traits.account_created_at);
		}
		amplitudeIdentify(id);
	},

	reset() {
		if (!ensureInit()) return;
		amplitudeReset();
	},
};
